// src/services/wildfireService.ts
// Frontend Wildfire Service - Makes API calls to backend wildfire router

import {
    WildfireEntity,
    WildfireQueryParams,
    WildfireResponse,
    CreateWildfireRequest,
    UpdateWildfireRequest,
    WildfireServiceError
} from '../types/wildfire';
import { logger } from '../utils/logger';
import { getBackendUrl, isDevMode } from './configService';

const log = logger('WildfireService');

/**
 * Frontend Wildfire Service
 * 
 * This service makes HTTP requests to the backend wildfire router.
 * It does NOT use UDL directly (that's for backend only).
 */
export class WildfireService {
    private getBaseUrl(): string {
        const backendUrl = getBackendUrl();
        if (!backendUrl) {
            throw new Error('❌ FATAL: BACKEND_URL not configured in runtime config');
        }
        return `${backendUrl}/api/wildfires`;
    }

    // Authentication token helper
    private getAuthToken(): string | null {
        const accessToken = localStorage.getItem('access_token');

        if (!accessToken) {
            if (isDevMode()) {
                return 'dev-mode-token';
            } else {
                log.debug('🚨 WildfireService: No access token found in production mode. Fetch will not be authenticated via Bearer token.');
                return null;
            }
        }
        return accessToken;
    }

    /**
     * Fetch wildfires with query parameters
     */
    async fetchData(params: WildfireQueryParams = {}): Promise<WildfireResponse> {
        try {
            log.debug('🔥 WildfireService: Fetching wildfires', params);

            // Build query string from parameters
            const queryParams = new URLSearchParams();

            if (params.page) queryParams.append('page', params.page.toString());
            if (params.limit) queryParams.append('limit', params.limit.toString());
            if (params.search) queryParams.append('search', params.search);
            if (params.region) queryParams.append('region', params.region);
            if (params.status) queryParams.append('status', params.status);
            if (params.response) queryParams.append('response', params.response);   // #771
            if (params.cause) queryParams.append('cause', params.cause);
            if (params.sortBy) queryParams.append('sortBy', params.sortBy);
            if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
            if (params.startDateFrom) queryParams.append('startDateFrom', params.startDateFrom);
            if (params.startDateTo) queryParams.append('startDateTo', params.startDateTo);
            if (params.sizeMin != null) queryParams.append('sizeMin', params.sizeMin.toString());
            if (params.sizeMax != null) queryParams.append('sizeMax', params.sizeMax.toString());
            if (params.number) queryParams.append('number', params.number);
            if (params.subCause) queryParams.append('subCause', params.subCause);
            if (params.objective) queryParams.append('objective', params.objective);
            if (params.outDateFrom) queryParams.append('outDateFrom', params.outDateFrom);
            if (params.outDateTo) queryParams.append('outDateTo', params.outDateTo);
            if (params.discoverySizeMin != null) queryParams.append('discoverySizeMin', params.discoverySizeMin.toString());
            if (params.discoverySizeMax != null) queryParams.append('discoverySizeMax', params.discoverySizeMax.toString());
            if (params.outSizeMin != null) queryParams.append('outSizeMin', params.outSizeMin.toString());
            if (params.outSizeMax != null) queryParams.append('outSizeMax', params.outSizeMax.toString());
            if (params.estimatedSizeMin != null) queryParams.append('estimatedSizeMin', params.estimatedSizeMin.toString());
            if (params.estimatedSizeMax != null) queryParams.append('estimatedSizeMax', params.estimatedSizeMax.toString());

            const url = `${this.getBaseUrl()}?${queryParams.toString()}`;
            log.debug('🔥 WildfireService: API URL', url);

            // ✅ ADD: Get auth token
            const token = this.getAuthToken();

            // ✅ ADD: Build headers with auth token
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // ✅ ADD: Include credentials in fetch
            const response = await fetch(url, {
                method: 'GET',
                headers,
                credentials: 'include',  // ✅ Send cookies!
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            log.debug(`🔥 WildfireService: Received ${data.data?.length || 0} wildfires`);

            return data;

        } catch (error) {
            log.error('🔥 WildfireService: Fetch error', error);
            throw this.createServiceError('FETCH_ERROR', 'Failed to fetch wildfires', error);
        }
    }

    /**
     * Get single wildfire by ID
     */
    async getById(id: string): Promise<WildfireEntity> {
        try {
            log.debug('🔥 WildfireService: Getting wildfire by ID', id);

            // ✅ ADD: Auth token and headers
            const token = this.getAuthToken();
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${this.getBaseUrl()}/${id}`, {
                method: 'GET',
                headers,
                credentials: 'include',
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw this.createServiceError('NOT_FOUND', `Wildfire with ID ${id} not found`);
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const wildfire = await response.json();
            log.debug('🔥 WildfireService: Retrieved wildfire', wildfire.fire_number);

            return wildfire;

        } catch (error) {
            log.error('🔥 WildfireService: Get by ID error', error);
            throw error instanceof Error && error.message.includes('NOT_FOUND')
                ? error
                : this.createServiceError('FETCH_ERROR', 'Failed to get wildfire', error);
        }
    }

    /**
     * Create new wildfire
     */
    async create(data: CreateWildfireRequest): Promise<WildfireEntity> {
        try {
            log.debug('🔥 WildfireService: Creating wildfire', data.fire_number);

            // ✅ ADD: Auth token and headers
            const token = this.getAuthToken();
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(this.getBaseUrl(), {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
                credentials: 'include',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
            }

            const wildfire = await response.json();
            log.debug('🔥 WildfireService: Created wildfire', wildfire.fire_number);

            return wildfire;

        } catch (error) {
            log.error('🔥 WildfireService: Create error', error);
            throw this.createServiceError('CREATE_ERROR', 'Failed to create wildfire', error);
        }
    }

    /**
     * Update existing wildfire
     */
    async update(id: string, updates: UpdateWildfireRequest): Promise<WildfireEntity> {
        try {
            log.debug('🔥 WildfireService: Updating wildfire', id);

            // ✅ ADD: Auth token and headers
            const token = this.getAuthToken();
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${this.getBaseUrl()}/${id}`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(updates),
                credentials: 'include',
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw this.createServiceError('NOT_FOUND', `Wildfire with ID ${id} not found`);
                }
                const errorData = await response.json().catch(() => ({}));
                throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
            }

            const wildfire = await response.json();
            log.debug('🔥 WildfireService: Updated wildfire', wildfire.fire_number);

            return wildfire;

        } catch (error) {
            log.error('🔥 WildfireService: Update error', error);
            throw error instanceof Error && error.message.includes('NOT_FOUND')
                ? error
                : this.createServiceError('UPDATE_ERROR', 'Failed to update wildfire', error);
        }
    }

    /**
     * Delete wildfire
     */
    async delete(id: string): Promise<boolean> {
        try {
            log.debug('🔥 WildfireService: Deleting wildfire', id);

            // ✅ ADD: Auth token and headers
            const token = this.getAuthToken();
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${this.getBaseUrl()}/${id}`, {
                method: 'DELETE',
                headers,
                credentials: 'include',
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw this.createServiceError('NOT_FOUND', `Wildfire with ID ${id} not found`);
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            log.debug('🔥 WildfireService: Deleted wildfire', id);
            return true;

        } catch (error) {
            log.error('🔥 WildfireService: Delete error', error);
            throw error instanceof Error && error.message.includes('NOT_FOUND')
                ? error
                : this.createServiceError('DELETE_ERROR', 'Failed to delete wildfire', error);
        }
    }

    /**
     * Search wildfires with enhanced filters
     */
    async search(searchTerm: string, filters?: Partial<WildfireQueryParams>): Promise<WildfireResponse> {
        return this.fetchData({
            ...filters,
            search: searchTerm
        });
    }

    /**
     * Get wildfires by region
     */
    async getByRegion(region: string, limit?: number): Promise<WildfireResponse> {
        return this.fetchData({
            region,
            limit
        });
    }

    /**
     * Get wildfires by status
     */
    async getByStatus(status: string, limit?: number): Promise<WildfireResponse> {
        return this.fetchData({
            status,
            limit
        });
    }

    /**
     * Get active wildfires (out of control + under control)
     */
    async getActiveWildfires(limit?: number): Promise<WildfireResponse> {
        // Note: This would need backend support for complex status filters
        // For now, we'll get all and filter, but ideally backend should handle this
        try {
            const [outOfControl, underControl] = await Promise.all([
                this.getByStatus('OUT_OF_CONTROL', limit),
                this.getByStatus('UNDER_CONTROL', limit)
            ]);

            return {
                data: [...outOfControl.data, ...underControl.data],
                total: outOfControl.total + underControl.total,
                page: 1,
                limit: limit || 25,
                totalPages: Math.ceil((outOfControl.total + underControl.total) / (limit || 25))
            };

        } catch (error) {
            log.error('🔥 WildfireService: Get active wildfires error', error);
            throw this.createServiceError('FETCH_ERROR', 'Failed to get active wildfires', error);
        }
    }

    /**
     * Get wildfire statistics
     */
    async getStatistics(): Promise<any> {
        try {
            // This would typically be a separate endpoint, but for now we can aggregate
            const response = await this.fetchData({ limit: 1000 }); // Get a large sample

            const stats = {
                total: response.total,
                byStatus: {} as Record<string, number>,
                byRegion: {} as Record<string, number>,
                byCause: {} as Record<string, number>
            };

            // Aggregate statistics
            response.data.forEach(wildfire => {
                // By status
                stats.byStatus[wildfire.wildfire_status] = (stats.byStatus[wildfire.wildfire_status] || 0) + 1;

                // By region
                stats.byRegion[wildfire.region] = (stats.byRegion[wildfire.region] || 0) + 1;

                // By cause
                if (wildfire.cause) {
                    stats.byCause[wildfire.cause] = (stats.byCause[wildfire.cause] || 0) + 1;
                }
            });

            return stats;

        } catch (error) {
            log.error('🔥 WildfireService: Get statistics error', error);
            throw this.createServiceError('FETCH_ERROR', 'Failed to get wildfire statistics', error);
        }
    }

    /**
     * Create service error with consistent format
     */
    private createServiceError(type: string, message: string, originalError?: any): WildfireServiceError {
        return {
            type,
            message,
            originalError,
            timestamp: new Date().toISOString(),
            service: 'WildfireService'
        };
    }
}

// Create singleton instance
export const wildfireService = new WildfireService();

// Export default for easy importing
export default wildfireService;